import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Alert, Snackbar, Paper, Typography, CircularProgress, Button, Box, Chip, Divider, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Tooltip, IconButton, TextField, InputAdornment } from "@mui/material";
import ILog from "../../types/Log";
import LogService from "../../services/LogService";
import IAlert from "../../types/Alert";
import IUser from "../../types/User";
import Config from "../../utils/Config";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DownloadIcon from '@mui/icons-material/GetApp';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from '@mui/icons-material/Clear';
import * as constantes from "../../common/Constantes";
import ConfigUtils from "../../utils/Config";
import ConfigService from "../../services/ConfigService";
import { CONFIGTYPES } from "../../types/Config";
import StripeService from "../../services/StripeService";
import { IPack } from "../../types/Pack";
import AuthService from "../../services/AuthService";
import Utilities from "../../common/Utilities";
import { useStripe } from "@stripe/react-stripe-js";
import { IWAPack } from "../../types/WAPack";
import IInvoice from "../../types/Invoice";
import moment from 'moment';
import 'moment/dist/locale/es';
import { ISimpleUser } from "./Empleados";
import TablePagination from "../utilities/TablePagination";

moment.locale('es');

const WhatsApp = () => {
    const history = useNavigate();
    const stripe = useStripe();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<IUser>();
    const [waEnabled, setWaEnabled] = useState<Boolean>(ConfigUtils.getWaConfig() ?? false);
    const [loadingToggle, setLoadingToggle] = useState<boolean>(false);

    const [packs, setPacks] = useState<IPack[]>([]);
    const [selectedPack, setSelectedPack] = useState<IPack | null>(null);

    const [openBuyDialog, setOpenBuyDialog] = useState<boolean>(false);
    const [loadingBuy, setLoadingBuy] = useState<boolean>(false);

    const [userPacks, setUserPacks] = useState<IWAPack[]>([]);
    const [totalAvailable, setTotalAvailable] = useState<number>(0);
    const [sentThisMonth, setSentThisMonth] = useState<number>(0);
    const [sentLastMonth, setSentLastMonth] = useState<number>(0);
    const [nextExpiry, setNextExpiry] = useState<any>(null);

    const [openPacksDialog, setOpenPacksDialog] = useState<boolean>(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [packInvoices, setPackInvoices] = useState<IInvoice[]>([]);
    const [waMessages, setWAMessages] = useState<any[]>([]);

    const [showAll, setShowAll] = useState(false);
    const [page, setPage] = useState(0);
    const [pagePack, setPagePack] = useState(0);

    const [searchString, setSearchString] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const userId = Config.getUserId();
    const userRole: number = ConfigUtils.getUserRole();
    const citaReminder = ConfigUtils.getCitaReminder() ?? false;
    const sanitarios: ISimpleUser[] = useMemo(() => ConfigUtils.getSanitarios(), []);
    // Con el pago de la suscripción pendiente no se pueden comprar packs
    const paymentFailed = user?.subscription?.status === 'payment_failed';

    useEffect(() => {
      const load = async () => {
        try {
          const [userResponse, packsResponse, waPacksResponse, waMessagesResponse] = await Promise.all([
            AuthService.getUser(userId),
            StripeService.getAvailablePacks(),
            StripeService.getWAPacks(userId),
            StripeService.getWAMessages(userId)
          ]);
          setUser(userResponse.data);
          setPacks(packsResponse.data);
          setUserPacks(waPacksResponse.data.packs);
          setTotalAvailable(waPacksResponse.data.totalAvailable);
          setSentThisMonth(waPacksResponse.data.sentThisMonth);
          setSentLastMonth(waPacksResponse.data.sentPastMonth);
          setNextExpiry(waPacksResponse.data.nextExpiry);
          setPackInvoices(waPacksResponse.data.invoices);
          setWAMessages(waMessagesResponse.data);
        } catch (error: any) {
          setAlert({ open: true, severity: 'error', msg: error.message });
        } finally {
          setLoading(false);
        }
      };
      load();
    }, [userId]);

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const handleToggle = async (newValue: boolean) => {
      setLoadingToggle(true);
      try {
        await ConfigService.updateWaConfig(ConfigUtils.getUserId(), { enabled: newValue });
        setWaEnabled(newValue);
        setAlert({ open: true, severity: "success", msg: "Configuración actualizada" });
        ConfigUtils.editConfig(CONFIGTYPES.waConfig, newValue);
        window.dispatchEvent(new Event('popstate'));
      } catch (e: any) {
        setAlert({ open: true, severity: 'error', msg: 'Error al actualizar la configuración' });
      } finally {
        setLoadingToggle(false);
      }
    };

    // El 3D Secure se ha abandonado o ha fallado: se anula en el servidor la factura del pack pendiente (sin hacer esperar al usuario)
    const cancelPendingPack = async (invoiceId?: string) => {
      if (!invoiceId) return;
      try {
        await StripeService.cancelWAPack(invoiceId);
      } catch (err) {
        const errorLog: ILog = { method: 'WhatsApp.Component.cancelPendingPack', level: 'error', message: err instanceof Error ? err.message : 'Error al anular el pack pendiente', meta: err };
        LogService.logError(errorLog);
      }
    };

    const handleBuy = async () => {
      if (!selectedPack) return;
      const previousTotalAvailable = totalAvailable;
      setLoadingBuy(true);
      try {
        const response = await StripeService.purchaseWAPack(selectedPack.priceId);
        const { clientSecret, confirmed, invoiceId } = response.data;
      
        if (confirmed) {
          setOpenPacksDialog(false);
          setOpenBuyDialog(false);
          setSelectedPack(null);
          await waitForDashboardUpdate(previousTotalAvailable);
          setAlert({ open: true, severity: 'success', msg: `Pack de ${selectedPack.msgs} mensajes comprado correctamente` });
          return;
        }
      
        if (!stripe) {
          setAlert({ open: true, severity: 'error', msg: 'Stripe no está inicializado. Recarga la página.' });
          return;
        }
      
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
      
        if (error) {
          const errorLog: ILog = { method: 'WhatsApp.Component.handleBuy', level: 'error', message: error.message || '', meta: error };
          LogService.logError(errorLog);
          setAlert({ open: true, severity: 'error', msg: error.message || 'Error en la autenticación 3D Secure.' });
          cancelPendingPack(invoiceId);
          return;
        }

        if (paymentIntent?.status === 'succeeded') {
          setOpenPacksDialog(false);
          setOpenBuyDialog(false);
          setSelectedPack(null);
          await waitForDashboardUpdate(previousTotalAvailable);
          setAlert({ open: true, severity: 'success', msg: `Pack de ${selectedPack.msgs} mensajes comprado correctamente` });
        } else {
          setAlert({ open: true, severity: 'error', msg: 'No se ha podido completar el pago.' });
          cancelPendingPack(invoiceId);
        }
      
      } catch (err: any) {
        if (err.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = { method: 'WhatsApp.Component.handleBuy', level: 'error', message: err.message, meta: err.response };
          LogService.logError(error);
          setAlert({ open: true, severity: 'error', msg: err.response?.data?.message || err.message });
        }
      } finally {
        setLoadingBuy(false);
      }
    };

    const updateDashboard = async () => {
      try {
          const [waPacksResponse, waMessagesResponse] = await Promise.all([
            StripeService.getWAPacks(userId),
            StripeService.getWAMessages(userId),
          ]);
          setTotalAvailable(waPacksResponse.data.totalAvailable);
          setUserPacks(waPacksResponse.data.packs);
          setSentThisMonth(waPacksResponse.data.sentThisMonth);
          setSentLastMonth(waPacksResponse.data.sentPastMonth);
          setNextExpiry(waPacksResponse.data.nextExpiry);
          setPackInvoices(waPacksResponse.data.invoices)
          setWAMessages(waMessagesResponse.data);
          return waPacksResponse.data;
        } catch (error: any) {
          setAlert({ open: true, severity: 'error', msg: error.message });
        }
    };

    const handleDownload = (invoiceId: string) => {
      setDownloadingId(invoiceId);
      StripeService.downloadInvoice(invoiceId)
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const invoiceNumber = packInvoices.find(i => i.invoiceId === invoiceId)?.number;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Invoice-${invoiceNumber}`;

        link.click();

        URL.revokeObjectURL(link.href);
      })
      .catch((e: any) => {
        if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'Suscripcion.Component.handleDownload', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
      })
      .finally(() => {
        setDownloadingId(null);
      });
    };

    const sleep = (ms: number) =>
      new Promise(resolve => setTimeout(resolve, ms));    

    const waitForDashboardUpdate = async (
      previousTotalAvailable: number,
      retries = 10,
      delay = 1000
    ) => {
      for (let i = 0; i < retries; i++) {
        try {
          const data = await updateDashboard();   

          if (data.totalAvailable > previousTotalAvailable) {
            return true;
          }
        } catch (e) {
          // ignoramos un fallo puntual y seguimos esperando
        }   

        await sleep(delay);
      }   

      return false;
    };

    const getCitaLabel = (date: string | Date, especialista?: string) => {
      const cita = moment(date);
      const hora = cita.format('HH:mm');
      const diff = cita.clone().startOf('day').diff(moment().startOf('day'), 'days');
        
      let dia;
      if (diff === 0) {
        dia = 'Hoy';
      } else if (diff === 1) {
        dia = 'Mañana';
      } else if (diff === -1) {
        dia = 'Ayer';
      } else {
        // Siempre mostrar día de la semana + día del mes para evitar ambigüedad
        dia = cita.format('dddd D [de] MMMM');
      }
    
      dia = dia.charAt(0).toUpperCase() + dia.slice(1);
      return `${dia} a las ${hora}${especialista ? ` con ${especialista}` : ''}`;
    };

    const clearFilter = () => {
      setSearchString('');
      setPage(0);
      searchInputRef.current?.focus();
    };

    const search = (e: any) => {
      try {
          const rawValue = e.target.value;
          setSearchString(rawValue);
          setPage(0);
      } catch (e: any) {
          const error: ILog = { method: 'WhatsApp.Component.search', level: 'error', message: e.message, meta: e.response };
          LogService.logError(error);
      }
    };

    const walletTotal = userPacks
      .filter(p => new Date(p.expiresAt) > new Date())
      .reduce((acc, p) => acc + p.packSize, 0);

    const walletUsed = userPacks
      .filter(p => new Date(p.expiresAt) > new Date())
      .reduce((acc, p) => acc + p.messagesUsed, 0);

    const usagePercent = walletTotal === 0 ? 100 : Math.round((walletUsed / walletTotal) * 100);

    const progressColor =
      usagePercent >= 100
        ? 'error'
        : usagePercent >= 80
          ? 'warning'
          : 'success';

    const diff = sentThisMonth - sentLastMonth;

    const diffPercent =
      sentLastMonth > 0
        ? Math.round((diff / sentLastMonth) * 100)
        : null;

    const trend =
      diff > 0 ? 'up' :
      diff < 0 ? 'down' :
      'flat';

    const daysRemaining = nextExpiry
      ? Math.max(
          0,
          Math.ceil(
            (new Date(nextExpiry.expiresAt).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24)
          )
        )
      : null;

    const metricCardSx = {
      p: 1.5,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    };

    const statusConfig: Record<
      string,
      {
        label: string;
        color: 'success' | 'info' | 'warning' | 'error' | 'default';
      }
    > = {
      sent: { label: 'Enviado', color: 'info' },
      delivered: { label: 'Entregado', color: 'success' },
      read: { label: 'Leído', color: 'success' },
      failed: { label: 'Fallido', color: 'error' },
      discarded: { label: 'Descartado', color: 'default' },
      pending: { label: 'Pendiente', color: 'warning' },
    };
    
    const discardLabels: Record<string, string> = {
      no_consent: 'Sin consentimiento WA',
      no_mobile_phone: 'Sin teléfono móvil',
      no_credit: 'Sin créditos',
      module_inactive: 'Módulo inactivo',
      invalid_phone: 'Teléfono inválido'
    };

    const twilioErrors: Record<number, string> = {
      63016: 'Número sin WhatsApp',
      30003: 'Número inaccesible temporalmente',
      30005: 'Número de teléfono no existe',
      30006: 'El número registrado es un teléfono fijo',
    };

    const getDetalle = (msg: any): string | null => {
      if (msg.discard_reason) return discardLabels[msg.discard_reason] ?? msg.discard_reason;
      if (msg.errorCode)   return twilioErrors[msg.errorCode] ?? 'Error desconocido';
      return null;
    };

    const formatFecha = (date: string | Date | null | undefined, opts?: Intl.DateTimeFormatOptions) =>
      date
        ? new Date(date).toLocaleDateString('es-ES', opts ?? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    dayAfterTomorrow.setHours(0, 0, 0, 0);

    const filteredMessages = showAll
      ? waMessages
      : waMessages.filter(msg => {
          const citaDate = msg.cita?.start ? new Date(msg.cita.start) : null;
          return citaDate && citaDate >= today && citaDate < dayAfterTomorrow;
        });

    const filterPacienteMessages = filteredMessages.filter(msg => {
      if (!searchString.trim()) return true;
      const textoBusqueda = Utilities.normalizeText(searchString);
      const nombre = Utilities.normalizeText(msg.paciente?.nombre ?? '');
      return nombre.includes(textoBusqueda);
    });
      
    const visibleMessages = filterPacienteMessages.slice(
      page * constantes.rowsPerPage,
      page * constantes.rowsPerPage + constantes.rowsPerPage
    );

    const packsPaginados = userPacks
      .sort(
        (a, b) => {
          const now = new Date();
          const aExpired = new Date(a.expiresAt) < now;
          const bExpired = new Date(b.expiresAt) < now;
          const aExhausted = !aExpired && a.messagesUsed >= a.packSize;
          const bExhausted = !bExpired && b.messagesUsed >= b.packSize;

          // Caducados al final
          if (aExpired && !bExpired) return 1;
          if (!aExpired && bExpired) return -1;

          // Agotados antes de caducados pero después de activos
          if (aExhausted && !bExhausted) return 1;
          if (!aExhausted && bExhausted) return -1;

          // Activos con saldo: FIFO por expiresAt
          return new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
        })
      .slice
      (
        pagePack * constantes.rowsPerPage,
        pagePack * constantes.rowsPerPage + constantes.rowsPerPage
      );
      
    try {
      return (
        userRole == constantes.ROLES.ADMINISTRADOR ?
          <Grid sx={{ marginX: 2 }}>
            <Paper elevation={3} sx={{ marginTop: 2, marginBottom: 3, padding: 3, display: 'flex', flexDirection: 'column' }}>
                {/* CABECERA */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <WhatsAppIcon sx={{ color: '#25D366', fontSize: 32 }} />
                
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      Recordatorios por WhatsApp
                    </Typography>
                
                    <Chip
                      label="Nuevo"
                      size="small"
                      color="success"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  </Box>
                
                  <FormControlLabel
                    control={
                      <Switch
                        checked={waEnabled as boolean}
                        disabled={loadingToggle}
                        onChange={(e) => handleToggle(e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      loadingToggle
                        ? 'Guardando...'
                        : waEnabled
                          ? 'Activado'
                          : 'Desactivado'
                    }
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Box>
                  
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Envío automático de recordatorios por WhatsApp el día antes de cada cita.
                  {citaReminder &&
                    ' Si el envío por WhatsApp no es posible, se usa el email como alternativa.'}
                </Typography>

                {/* BANNER módulo inactivo */}
                {!waEnabled && (
                  <Grid item xs={12} sx={{ mb: 1 }}>
                    <Alert severity="info">
                      El módulo de WhatsApp está desactivado. Actívalo para empezar a enviar recordatorios automáticos por WhatsApp.
                    </Alert>
                  </Grid>
                )}  

                {/* BANNER pago de la suscripción pendiente */}
                {paymentFailed && (
                  <Grid item xs={12} sx={{ mb: 1 }}>
                    <Alert
                      severity="warning"
                      action={<Button color="inherit" size="small" onClick={() => history(constantes.R_SUSCRIPCION)}>Ir a Suscripción</Button>}
                    >
                      {user?.subscription?.paymentFailedReason === 'authentication_required'
                        ? 'No puedes comprar packs mientras tengas un pago de tu suscripción pendiente de confirmar. Confirma el pago desde Suscripción y vuelve a intentarlo.'
                        : 'No puedes comprar packs mientras tengas el pago de tu suscripción pendiente. Actualiza tu método de pago desde Suscripción y vuelve a intentarlo.'}
                    </Alert>
                  </Grid>
                )}
                  
                <Divider sx={{ mb: 3 }} />
              
                  {loading ? <CircularProgress style={{ margin: '20px auto' }} /> :
                    userPacks.length > 0 ? <Grid container spacing={3}>               

                    {/* MÉTRICAS */}
                    <Grid item xs={12}>
                      <Grid container spacing={2} alignItems="stretch">
                        {/* MENSAJES DISPONIBLES */}
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={metricCardSx}>
                            <Typography variant="caption" color="text.secondary">
                              Mensajes disponibles
                            </Typography>
                            <Typography
                              variant="h3"
                              fontWeight="bold"
                              color={totalAvailable > 0 ? 'success.main' : 'text.disabled'}
                              sx={{ my: 0.5 }}
                            >
                              {totalAvailable.toLocaleString('es-ES')}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent}
                              color={progressColor}
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ mt: 'auto' }}>
                              <Typography variant="caption" color="text.secondary">
                                {walletUsed.toLocaleString('es-ES')} usados de{' '}
                                {walletTotal.toLocaleString('es-ES')}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                        {/* ENVIADOS ESTE MES */}
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={metricCardSx}>
                            <Typography variant="caption" color="text.secondary">
                              Enviados este mes
                            </Typography>
                            <Typography variant="h3" fontWeight="bold" sx={{ my: 0.5 }}>
                              {sentThisMonth.toLocaleString('es-ES')}
                            </Typography>
                            <Box sx={{ mt: 'auto' }}>
                              {sentLastMonth > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {trend === 'up' && (
                                    <TrendingUpIcon fontSize="small" color="success" />
                                  )}
                                  {trend === 'down' && (
                                    <TrendingDownIcon
                                      fontSize="small"
                                      sx={{ color: 'text.secondary' }}
                                    />
                                  )}
                                  {trend === 'flat' && (
                                    <TrendingFlatIcon
                                      fontSize="small"
                                      sx={{ color: 'text.secondary' }}
                                    />
                                  )}
                                  <Typography
                                    variant="caption"
                                    color={trend === 'up' ? 'success.main' : 'text.secondary'}
                                  >
                                    {trend === 'flat'
                                      ? 'Sin cambios respecto al mes pasado'
                                      : `${diff > 0 ? '+' : ''}${diffPercent}% (${diff > 0 ? '+' : ''}${diff} mensajes)`}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Primer mes de uso
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                            
                        {/* PRÓXIMA CADUCIDAD */}
                        <Grid item xs={12} sm={4}>
                          <Paper variant="outlined" sx={metricCardSx}>
                            <Typography variant="caption" color="text.secondary">
                              Próxima caducidad
                            </Typography>
                            
                            {nextExpiry ? (
                              <>
                                <Typography variant="h3" fontWeight="bold" sx={{ my: 0.5 }}>
                                  {daysRemaining} días
                                </Typography>
                            
                                <Box sx={{ mt: 'auto' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(nextExpiry.expiresAt).toLocaleDateString('es-ES', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    })}
                                    {' · '}
                                    {nextExpiry.messagesRemaining} mensajes restantes
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Typography variant="h3" color="text.disabled" sx={{ my: 0.5 }}>
                                  —
                                </Typography>
                            
                                <Box sx={{ mt: 'auto' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    No hay packs activos
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Paper>
                        </Grid>
                          
                      </Grid>
                    </Grid>

                    {/* BANNER GLOBAL DE ALERTAS */}
                    {(() => {
                      const now = new Date();
                      const hasExpiring = userPacks.some(p => {
                        const exp = new Date(p.expiresAt);
                        return exp > now &&
                          exp < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) &&
                          p.messagesUsed < p.packSize;
                      });
                    
                      if (totalAvailable === 0) return (
                        <Grid item xs={12}>
                          <Alert severity="error" action={
                            <Button color="inherit" size="small" disabled={paymentFailed} onClick={() => setOpenPacksDialog(true)}>
                              Comprar mensajes
                            </Button>
                          }>
                            Sin créditos disponibles. Los recordatorios por WhatsApp están suspendidos.
                          </Alert>
                        </Grid>
                      );
                    
                      if (usagePercent >= 90) return (
                        <Grid item xs={12}>
                          <Alert severity="error" action={
                            <Button color="inherit" size="small" disabled={paymentFailed} onClick={() => setOpenPacksDialog(true)}>
                              Comprar mensajes
                            </Button>
                          }>
                            Quedan solo {totalAvailable} mensajes disponibles. Compra un pack para evitar interrupciones.
                          </Alert>
                        </Grid>
                      );
                    
                      if (usagePercent >= 80) return (
                        <Grid item xs={12}>
                          <Alert severity="warning" action={
                            <Button color="inherit" size="small" disabled={paymentFailed} onClick={() => setOpenPacksDialog(true)}>
                              Comprar mensajes
                            </Button>
                          }>
                            Has usado el {usagePercent}% de tus mensajes. Quedan {totalAvailable} disponibles.
                          </Alert>
                        </Grid>
                      );
                    
                      if (hasExpiring) return (
                        <Grid item xs={12}>
                          <Alert severity="warning">
                            Tienes packs próximos a caducar en menos de 30 días. Revisa la sección de packs comprados.
                          </Alert>
                        </Grid>
                      );
                    
                      return null;
                    })()}

                    {/* HISTORIAL DE MENSAJES */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Mensajes enviados
                            </Typography>
                            {waMessages.length > 0 && (
                              <Button size="small" variant="text" onClick={() => { setShowAll(!showAll); setPage(0); }}>
                                {showAll ? 'Ver hoy y mañana' : `Ver todos (${waMessages.length})`}
                              </Button>
                            )}
                          </Box>
                          
                          {waMessages.length > 0 && (
                            <TextField
                              placeholder="Buscar por paciente..."
                              size="small"
                              inputRef={searchInputRef}
                              onChange={search}
                              value={searchString}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                                endAdornment: searchString ? (
                                  <InputAdornment position="end">
                                    <IconButton onClick={clearFilter} size="small">
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null
                              }}
                              sx={{ width: { xs: '100%', sm: '300px' } }}
                            />
                          )}
                        </Box>

                        {waMessages.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                              El historial de mensajes estará disponible cuando se realicen los primeros envíos.
                            </Typography>
                          ) : filterPacienteMessages.length === 0 && searchString.trim() ? (
                            // Sin resultados de búsqueda
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No se encontraron mensajes para "{searchString}".
                              </Typography>
                            </Box>
                          ) : visibleMessages.length === 0 ? (
                            // Sin citas hoy/mañana
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No hay recordatorios para las citas de hoy ni de mañana.
                              </Typography>
                              <Button size="small" variant="text" onClick={() => { setShowAll(true); setPage(0); }}>
                                Ver historial completo
                              </Button>
                            </Box> ):
                          <>
                            {/* TABAL - Desktop y  tablet */}
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Paciente</TableCell>
                                    <TableCell>Cita</TableCell>
                                    <TableCell>Enviado</TableCell>
                                    <TableCell align="right">Estado</TableCell>
                                    <TableCell align="right">Detalle</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {visibleMessages.map((msg, index) => {
                                    const s = statusConfig[msg.status] ?? { label: msg.status, color: 'default' as const };
                                    const detalle = getDetalle(msg);
                                    const especialista = sanitarios.find(s => s.internalID === msg.cita?.especialistaId);
                                    const citaFecha = msg.cita?.start
                                      ? getCitaLabel(msg.cita.start, especialista?.firstName)
                                      : '—';
                                    return (
                                      <TableRow key={index} hover>
                                        <TableCell>
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                            component="a"
                                            href={`${constantes.R_PACIENTE}/?${constantes.QP_ID}=${msg.paciente?.id}`}
                                            sx={{
                                              textDecoration: 'none',
                                              color: 'currentColor',
                                              '&:hover': {
                                                textDecoration: 'underline',
                                              },
                                            }}
                                          >
                                            {msg.paciente?.nombre ?? '—'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {citaFecha}
                                        </TableCell>
                                        <TableCell>
                                          {msg.sentAt
                                            ? formatFecha(msg.sentAt, {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })
                                            : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Chip label={s.label} size="small" color={s.color} />
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="caption" color={msg.errorMessage ? 'error' : 'text.secondary'}>
                                            {detalle ?? '—'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                            {/* CARDS — mobile */}
                            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1 }}>
                              {visibleMessages.map((msg, index) => {
                                const s = statusConfig[msg.status] ?? { label: msg.status, color: 'default' as const };
                                const detalle = getDetalle(msg);
                                const especialista = sanitarios.find(s => s.internalID === msg.cita?.especialistaId);
                                const citaFecha = msg.cita?.start
                                      ? getCitaLabel(msg.cita.start, especialista?.firstName)
                                      : '—';
                                return (
                                  <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <Box>
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight="bold"
                                          component="a"
                                          href={`${constantes.R_PACIENTE}/?${constantes.QP_ID}=${msg.paciente?.id}`}
                                          sx={{
                                            textDecoration: 'none',
                                            color: 'currentColor',
                                            '&:hover': {
                                              textDecoration: 'underline',
                                            },
                                          }}
                                        >
                                          {msg.paciente?.nombre ?? '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">Cita: {citaFecha}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Enviado: {formatFecha(msg.sentAt ?? msg.createdAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                        {detalle && (
                                          <Typography variant="caption" color={msg.errorMessage ? 'error' : 'text.secondary'} display="block">
                                            {detalle}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Chip label={s.label} size="small" color={s.color} />
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Box>

                            {/* PAGINACIÓN */}
                            <TablePagination
                              count={filterPacienteMessages.length}
                              page={page}
                              rowsPerPage={constantes.rowsPerPage}
                              onPageChange={setPage}
                            />
                          </>
                        }
                      </Paper>
                    </Grid>                 

                    {/* PACKS COMPRADOS */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexWrap: 'wrap',
                            gap: 2,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            Packs comprados
                          </Typography>
                        
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={<ShoppingCartIcon />}
                            disabled={!waEnabled || paymentFailed}
                            onClick={() => setOpenPacksDialog(true)}
                          >
                            Comprar mensajes
                          </Button>
                        </Box>
                        
                        {userPacks.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            No hay packs comprados todavía.
                          </Typography>
                        ) : (
                          <>
                          <Grid container spacing={2}>
                            {packsPaginados.map((pack, index) => {
                              const now = new Date();
                              const expiresAt = new Date(pack.expiresAt);
                              const isExpired = expiresAt < now;
                              const isExpiring = !isExpired && expiresAt < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                              const remaining = pack.packSize - pack.messagesUsed;
                              const isExhausted = remaining === 0;
                              const usagePackPercent = Math.round((pack.messagesUsed / pack.packSize) * 100);
                              const isNearLimit90 = !isExpired && !isExhausted && usagePackPercent >= 90;
                              const isNearLimit80 = !isExpired && !isExhausted && !isNearLimit90 && usagePackPercent >= 80;

                              const invoice = packInvoices?.find(inv => inv.invoiceId === pack.invoiceId);

                              return (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      borderColor: isNearLimit90 ? 'error.light'
                                        : isNearLimit80 ? 'warning.main'
                                        : isExpiring ? 'warning.light'
                                        : 'divider',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        {pack.packSize} mensajes
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                          label={
                                            isExhausted ? 'Agotado'
                                            : isExpired ? 'Caducado'
                                            : isExpiring ? 'Caduca pronto'
                                            : 'Activo'
                                          }
                                          color={
                                            isExhausted ? 'error'
                                            : isExpired ? 'default'
                                            : isExpiring ? 'warning'
                                            : 'success'
                                          }
                                          size="small"
                                        />
                                        {invoice && (
                                          <Tooltip title="Descargar factura">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleDownload(pack.invoiceId)}
                                              disabled={downloadingId === pack.invoiceId}
                                            >
                                              {downloadingId === pack.invoiceId
                                                ? <CircularProgress size={16} />
                                                : <DownloadIcon fontSize="small" />
                                              }
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </Box>
                                      
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                      {isExpired
                                        ? `Caducó el ${expiresAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                        : `Caduca el ${expiresAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
                                      }
                                    </Typography>
                                      
                                    <LinearProgress
                                      variant="determinate"
                                      value={isExpired ? 100 : usagePackPercent}
                                      color={
                                        isExpired || isExhausted ? 'error'
                                        : isNearLimit90 ? 'error'
                                        : isNearLimit80 ? 'warning'
                                        : 'success'
                                      }
                                      sx={{ mb: 1, borderRadius: 1 }}
                                    />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isExpired || isExhausted || isNearLimit80 ? 1 : 0 }}>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Usados</Typography>
                                        <Typography variant="body2">{pack.messagesUsed}</Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Restantes</Typography>
                                        <Typography variant="body2" color={remaining > 0 ? 'success.main' : 'error.main'}>
                                          {remaining}
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">Uso</Typography>
                                        <Typography variant="body2">{isExpired ? '—' : `${usagePackPercent}%`}</Typography>
                                      </Box>
                                    </Box>

                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                          {/* PAGINACIÓN */}
                          <TablePagination
                            count={userPacks.length}
                            page={pagePack}
                            rowsPerPage={constantes.rowsPerPage}
                            onPageChange={setPagePack}
                          />
                          </>
                        )}
                      </Paper>
                    </Grid>      

                    {/* DIALOG - Selección del pack */}
                    {openPacksDialog && 
                    <Dialog open={openPacksDialog} onClose={()=> { setSelectedPack(null); setOpenPacksDialog(false) }}>
                      <Grid item xs={12}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            borderStyle: 'dashed',
                            opacity: waEnabled ? 1 : 0.5,
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                            Empieza a enviar recordatorios
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Compra tu primer pack de mensajes para activar los envíos automáticos por WhatsApp.
                          </Typography>
                          <Grid container spacing={1} sx={{ mb: 3, maxWidth: 400 }}>
                            {packs.map((pack) => (
                              <Grid item xs={6} key={pack.priceId}>
                                <Paper
                                  variant="outlined"
                                  onClick={() => waEnabled && setSelectedPack(pack)}
                                  sx={{
                                    p: 1.5, textAlign: 'center',
                                    cursor: waEnabled ? 'pointer' : 'default',
                                    borderColor: selectedPack?.priceId === pack.priceId ? 'success.main' : 'divider',
                                    borderWidth: 2,
                                    bgcolor: selectedPack?.priceId === pack.priceId ? '#f0fdf4' : 'background.paper',
                                    transition: 'all 0.15s ease',
                                    '&:hover': waEnabled ? { borderColor: 'success.main', bgcolor: '#f0fdf4' } : {},
                                  }}
                                >
                                  <Typography variant="subtitle2" fontWeight="bold">{pack.msgs} mensajes</Typography>
                                  <Typography variant="body2" color="text.secondary">{pack.displayPrice}</Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                          <Button
                            variant="contained"
                            color="success"
                            size="large"
                            startIcon={<ShoppingCartIcon />}
                            disabled={!waEnabled || !selectedPack || paymentFailed}
                            onClick={() => setOpenBuyDialog(true)}
                          >
                            Comprar pack
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Sin permanencia · los mensajes caducan a los 12 meses
                          </Typography>
                        </Paper>
                      </Grid>
                    </Dialog>}                

                  </Grid> : 
                  <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        
                        {/* Mensajes disponibles */}
                        <Paper variant="outlined" sx={{ p: 2.5 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                            Mensajes disponibles
                          </Typography>
                          <Typography variant="h3" fontWeight="bold" color={0 > 0 ? 'success.main' : 'text.disabled'}>
                            0
                          </Typography>
                        </Paper>
                        
                      </Grid>
                        
                      <Grid item xs={12} md={6}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            borderStyle: 'dashed',
                            opacity: waEnabled ? 1 : 0.5,
                          }}
                        >
                          <ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                            Empieza a enviar recordatorios
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Compra tu primer pack de mensajes para activar los envíos automáticos por WhatsApp.
                          </Typography>
                      
                          {/* Packs disponibles */}
                          <Grid container spacing={1} sx={{ mb: 3 }}>
                            {packs.map((pack) => (
                              <Grid item xs={6} key={pack.priceId}>
                                <Paper
                                  variant="outlined"
                                  onClick={() => waEnabled && setSelectedPack(pack)}
                                  sx={{
                                    p: 1.5,
                                    textAlign: 'center',
                                    cursor: waEnabled ? 'pointer' : 'default',
                                    borderColor: selectedPack?.priceId === pack.priceId ? 'success.main' : 'divider',
                                    borderWidth: 2,
                                    bgcolor: selectedPack?.priceId === pack.priceId ? '#f0fdf4' : 'background.paper',
                                    transition: 'all 0.15s ease',
                                    '&:hover': waEnabled ? { borderColor: 'success.main', bgcolor: '#f0fdf4' } : {},
                                  }}
                                >
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {pack.msgs} mensajes
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {pack.displayPrice}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        
                          <Button
                            variant="contained"
                            color="success"
                            size="large"
                            fullWidth
                            startIcon={<ShoppingCartIcon />}
                            disabled={!waEnabled || !selectedPack || paymentFailed}
                            onClick={() => setOpenBuyDialog(true)}
                          >
                            Comprar pack
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Sin permanencia · los mensajes caducan a los 12 meses
                          </Typography>
                        </Paper>
                      </Grid>
                  </Grid>
                  }
                  
            </Paper>

            {/* DIALOG CONFIRMACIÓN DE COMPRA */}
            <Dialog
              open={openBuyDialog}
              onClose={() => !loadingBuy && setOpenBuyDialog(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Confirmar compra</DialogTitle>
              <DialogContent>
                {selectedPack && (
                  <Box sx={{ mt: 1 }}>
                  
                    <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 16 }}>
                      {`${selectedPack.msgs} mensajes de WhatsApp`}
                    </Typography>
                
                    <Typography>
                      <span style={{ fontWeight: 'bold', fontSize: 34 }}>
                        {Utilities.formatAmount(selectedPack.amount)} €
                      </span>
                      {' '}pago único
                    </Typography>
                
                    <Box sx={{ mb: 2, mt: 2 }}>
                      <Grid container>
                        <Grid item xs={9}>
                          <Typography variant="body1">Subtotal</Typography>
                          <Typography variant="body1">IVA (21%)</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {Utilities.formatAmount(selectedPack.amount / 1.21)} €
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {Utilities.formatAmount(selectedPack.amount - selectedPack.amount / 1.21)} €
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider />

                    <Box sx={{ mb: 2, mt: 2 }}>
                      <Grid container>
                        <Grid item xs={9}>
                          <Typography variant="body1">Total a pagar hoy</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 'bold' }}>
                            {Utilities.formatAmount(selectedPack.amount)} €
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider />
                            
                    {/* Método de pago */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CreditCardIcon sx={{ color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      •••• •••• •••• {user?.paymentMethod?.cardLast4}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Caduca {user?.paymentMethod?.cardExpMonth}/{user?.paymentMethod?.cardExpYear}
                    </Typography>
                  </Box>
                    </Box>
                            
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOpenBuyDialog(false)}
                  disabled={loadingBuy}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={loadingBuy}
                  onClick={handleBuy}
                >
                  {loadingBuy
                    ? <CircularProgress size={20} color="inherit" />
                    : 'Confirmar compra'
                  }
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
              <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
          </Grid>
          :
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open>
              <Alert severity="warning" sx={{ width: '100%' }} >Usuario no autorizado</Alert>
          </Snackbar>
      );
    } catch (e: any) {
      const error: ILog = { method: 'WhatsApp.Component', level: 'error', message: e.message, meta: e.message };
      LogService.logError(error);
      return (
          <Alert severity="error">{e.message}</Alert>
      );
    }

};

export default WhatsApp;